import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { 
  Description as FileTextIcon, 
  Folder as FolderIcon, 
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

// Define animations
const ping = keyframes`
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
`;

const bounce = keyframes`
  0%, 100% {
    transform: translateY(-25%);
    animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
  }
  50% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(180deg);
  }
`;

const EmptyState = () => {
  return (
    <Box
      sx={{
        width: '100%',
        minHeight: { xs: '500px', sm: '500px', md: '550px' },
        maxHeight: { xs: '600px', sm: '650px', md: '700px' },

        // height: { xs: '60vh', sm: '40vh', md: '80vh' },  //  responsive height
        // minHeight: 'calc(100vh - 70px)',  
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f6fa',
        p: 0,
        m: 0,
      }}
    >
      <Box sx={{ 
        width: '100%', 
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '8rem', sm: '12rem', md: '16rem' },
            height: { xs: '8rem', sm: '12rem', md: '16rem' },
            background: 'linear-gradient(to bottom right, rgba(29, 78, 216, 0.1), rgba(15, 118, 110, 0.1))',
            borderRadius: '50%',
            mixBlendMode: 'multiply',
            filter: 'blur(3rem)',
            opacity: 0.1,
            animation: `${pulse} 4s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
          }}
        />

        {/* Content Card - Full Width & Height */}
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(40px)',
            borderRadius: 0,
            // p: { xs: 3, sm: 4, md: 6, lg: 8 },
            p: { xs: 2.5, sm: 3, md: 4, lg: 6 },
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Icon Container with Enhanced Animation */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ position: 'relative' }}>
              {/* Multiple Animated Rings */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to bottom right, #1e3a8a, #1e40af)',
                  borderRadius: '50%',
                  opacity: 0.2,
                  animation: `${ping} 1s cubic-bezier(0, 0, 0.2, 1) infinite`,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: '0.5rem',
                  background: 'linear-gradient(to bottom right, #14b8a6, #16a34a)',
                  borderRadius: '50%',
                  opacity: 0.3,
                  animation: `${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
                }}
              />

              {/* Main Icon Circle with Gradient */}
              <Paper
                elevation={20}
                sx={{
                  position: 'relative',
                  background: 'linear-gradient(to bottom right, #1e3a8a, #1e40af, #0f766e)',
                  borderRadius: '50%',
                  // p: { xs: 2.5, sm: 3, md: 4 },
                   p: { xs: 2, sm: 2.5, md: 3 },
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <FolderIcon
                    sx={{
                      // fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem' },
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                      color: 'white',
                      filter: 'drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1))',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: { xs: '-0.25rem', sm: '-0.5rem' },
                      right: { xs: '-0.25rem', sm: '-0.5rem' },
                      background: 'linear-gradient(to bottom right, #14b8a6, #16a34a)',
                      borderRadius: '50%',
                      p: { xs: 0.3, sm: 0.5 },
                      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                      animation: `${bounce} 1s infinite`,
                    }}
                  >
                    <FileTextIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }, color: 'white' }} />
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* Text Content */}
          <Box sx={{ textAlign: 'center',mb: { xs: 2, sm: 2.5, md: 3 }, maxWidth: '600px', px: { xs: 2, sm: 0 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(to right, #1e40af, #1e3a8a, #0f766e)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: { xs: 1.5, sm: 2 },
                // fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.25rem' },
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.875rem', lg: '2rem' },
                letterSpacing: '-0.025em',
              }}
            >
             Processing your request... this may take just a moment.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#475569',
                // fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '1rem' },
                lineHeight: 1.75,
                px: { xs: 1, sm: 0 },
              }}
            >
             This won't take long - setting things up for you.
            </Typography>
          </Box>

          {/* Enhanced Feature Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              // gap: { xs: 2, sm: 2.5 },
              gap: { xs: 1.5, sm: 2 },
              mb: { xs: 2, sm: 2.5, md: 3 },
              maxWidth: '900px',
              width: '100%',
              px: { xs: 2, sm: 0 },
            }}
          >
            {/* View Documents Card */}
            <Paper
              elevation={2}
              sx={{
                background: 'linear-gradient(to bottom right, #eff6ff, #dbeafe, #eff6ff)',
                borderRadius: { xs: '0.75rem', sm: '1rem' },
                // p: { xs: 2, sm: 2.5 },
                p: { xs: 1.5, sm: 2 },
                textAlign: 'center',
                transition: 'all 0.3s',
                cursor: 'pointer',
                border: '1px solid rgba(191, 219, 254, 0.5)',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                },
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(to bottom right, white, #eff6ff)',
                  borderRadius: '50%',
                  width: { xs: '2.75rem', sm: '3.5rem' },
                  height: { xs: '2.75rem', sm: '3.5rem' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 1, sm: 1.5 },
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                  },
                }}
              >
                <FileTextIcon
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    color: '#1e3a8a',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, fontWeight: 600, color: '#334155', mb: 0.5 }}>
                View Documents
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.6875rem', sm: '0.75rem' }, color: '#64748b' }}>
                Access your files
              </Typography>
            </Paper>

            {/* Manage Files Card */}
            <Paper
              elevation={2}
              sx={{
                background: 'linear-gradient(to bottom right, #f9fafb, #f3f4f6, #f9fafb)',
                borderRadius: { xs: '0.75rem', sm: '1rem' },
                // p: { xs: 2, sm: 2.5 },
                 p: { xs: 1.5, sm: 2 },
                textAlign: 'center',
                transition: 'all 0.3s',
                cursor: 'pointer',
                border: '1px solid rgba(209, 213, 219, 0.5)',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                },
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(to bottom right, white, #f9fafb)',
                  borderRadius: '50%',
                  width: { xs: '2.75rem', sm: '3.5rem' },
                  height: { xs: '2.75rem', sm: '3.5rem' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 1, sm: 1.5 },
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                  },
                }}
              >
                <FolderIcon
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    color: '#374151',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, fontWeight: 600, color: '#334155', mb: 0.5 }}>
                Manage Files
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.6875rem', sm: '0.75rem' }, color: '#64748b' }}>
                Organize content
              </Typography>
            </Paper>

            {/* Update Data Card */}
            <Paper
              elevation={2}
              sx={{
                background: 'linear-gradient(to bottom right, #f0fdfa, #ccfbf1, #f0fdfa)',
                borderRadius: { xs: '0.75rem', sm: '1rem' },
                // p: { xs: 2, sm: 2.5 },
                 p: { xs: 1.5, sm: 2 },
                textAlign: 'center',
                transition: 'all 0.3s',
                cursor: 'pointer',
                border: '1px solid rgba(153, 246, 228, 0.5)',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                },
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(to bottom right, white, #f0fdfa)',
                  borderRadius: '50%',
                  width: { xs: '2.75rem', sm: '3.5rem' },
                  height: { xs: '2.75rem', sm: '3.5rem' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 1, sm: 1.5 },
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                  },
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    color: '#0d9488',
                    transition: 'transform 0.5s',
                    '&:hover': {
                      animation: `${rotate} 0.5s ease-in-out`,
                    },
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, fontWeight: 600, color: '#334155', mb: 0.5 }}>
                Update Data
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.6875rem', sm: '0.75rem' }, color: '#64748b' }}>
                Manually
              </Typography>
            </Paper>
          </Box>

          {/* Enhanced Call to Action */}
          <Box sx={{ display: 'flex', justifyContent: 'center', px: { xs: 2, sm: 0 } }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: { xs: 0.75, sm: 1 },
                color: 'white',
                fontWeight: 500,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                background: 'linear-gradient(to right, #1e3a8a, #1e40af, #0f766e)',
                px: { xs: 3, sm: 4 },
                py: { xs: 1.5, sm: 2 },
                borderRadius: '9999px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                '&:hover': {
                  boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, fontWeight: 500 }}>
                Select from dropdown above
              </Typography>
              <ArrowForwardIcon
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateX(0.25rem)',
                  },
                }}
              />
            </Box>
          </Box>

          {/* Enhanced Bottom Decorative Elements */}
          <Box sx={{ mt: { xs: 1.5, sm: 2 }, display: 'flex', justifyContent: 'center', gap: { xs: 1, sm: 1.5 } }}>
            <Box
              sx={{
                width: { xs: '0.5rem', sm: '0.625rem' },
                height: { xs: '0.5rem', sm: '0.625rem' },
                background: 'linear-gradient(to right, #1e3a8a, #1e40af)',
                borderRadius: '50%',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                animation: `${bounce} 1s infinite`,
              }}
            />
            <Box
              sx={{
                width: { xs: '0.5rem', sm: '0.625rem' },
                height: { xs: '0.5rem', sm: '0.625rem' },
                background: 'linear-gradient(to right, #4b5563, #374151)',
                borderRadius: '50%',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                animation: `${bounce} 1s infinite`,
                animationDelay: '0.1s',
              }}
            />
            <Box
              sx={{
                width: { xs: '0.5rem', sm: '0.625rem' },
                height: { xs: '0.5rem', sm: '0.625rem' },
                background: 'linear-gradient(to right, #14b8a6, #16a34a)',
                borderRadius: '50%',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                animation: `${bounce} 1s infinite`,
                animationDelay: '0.2s',
              }}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
export default EmptyState;